import React from 'react';
import { Order } from '../../types/order';
import { Clock, User, Package, CheckCircle, AlertCircle, UserPlus, X, UserMinus } from 'lucide-react';

interface OrderTableProps {
  orders: Order[];
  onOrderSelect: (order: Order) => void;
  onAssignOrder?: (order: Order) => void;
  onDeassignOrder?: (order: Order) => void;
  onCancelOrder?: (order: Order) => void;
  loading?: boolean;
  selectedOrderIds?: number[];
  onOrderSelectionChange?: (orderIds: number[]) => void;
}

const OrderTable: React.FC<OrderTableProps> = ({ 
  orders, 
  onOrderSelect, 
  onAssignOrder,
  onDeassignOrder,
  onCancelOrder,
  loading = false,
  selectedOrderIds = [],
  onOrderSelectionChange
}) => {
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-800', icon: User },
      picking: { label: 'Picking', color: 'bg-purple-100 text-purple-800', icon: Package },
      picked: { label: 'Picked', color: 'bg-indigo-100 text-indigo-800', icon: Package },
      verified: { label: 'Verified', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const canAssignOrder = (order: Order) => {
    return order.status === 'pending' && !order.assigned_worker && !order.assigned_worker_id;
  };

  const canDeassignOrder = (order: Order) => {
    return order.status === 'assigned' && (order.assigned_worker || order.assigned_worker_id);
  };

  const canCancelOrder = (order: Order) => {
    return ['pending', 'assigned'].includes(order.status);
  };

  const handleSelectAll = (checked: boolean) => {
    if (onOrderSelectionChange) {
      if (checked) {
        const selectableOrders = orders.filter(order => canAssignOrder(order) || canCancelOrder(order));
        onOrderSelectionChange(selectableOrders.map(order => order.id));
      } else {
        onOrderSelectionChange([]);
      }
    }
  };

  const handleSelectOrder = (orderId: number, checked: boolean) => {
    if (onOrderSelectionChange) {
      if (checked) {
        onOrderSelectionChange([...selectedOrderIds, orderId]);
      } else {
        onOrderSelectionChange(selectedOrderIds.filter(id => id !== orderId));
      }
    }
  };

  const allSelectableOrders = orders.filter(order => canAssignOrder(order) || canCancelOrder(order));
  const isAllSelected = selectedOrderIds.length > 0 && selectedOrderIds.length === allSelectableOrders.length;
  const isIndeterminate = selectedOrderIds.length > 0 && selectedOrderIds.length < allSelectableOrders.length;

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {onOrderSelectionChange && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    {selectedOrderIds.length > 0 && (
                      <span className="ml-2 text-xs text-blue-600 font-medium">
                        {selectedOrderIds.length}
                      </span>
                    )}
                  </div>
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned Worker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">              Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.map((order) => {
              const isSelectable = canAssignOrder(order) || canCancelOrder(order);
              const isSelected = selectedOrderIds.includes(order.id);
              
              return (
                <tr 
                  key={order.id} 
                  className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                >
                  {onOrderSelectionChange && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectOrder(order.id, e.target.checked)}
                        disabled={!isSelectable}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      />
                    </td>
                  )}
                  <td 
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onOrderSelect(order)}
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {order.order_number}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                      </div>
                      {order.priority > 1 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mt-1">
                          High Priority
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.total_items} items
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.items.slice(0, 2).map(item => `${item.sku.split('-')[0]}`).join(', ')}
                      {order.items.length > 2 && '...'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      {order.assigned_worker ? (
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-green-500 mr-1" />
                          <span>{order.assigned_worker.full_name}</span>
                        </div>
                      ) : order.assigned_worker_name ? (
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-green-500 mr-1" />
                          <span>{order.assigned_worker_name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(order.order_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {canAssignOrder(order) && onAssignOrder && (
                        <button
                          onClick={() => onAssignOrder(order)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="Assign to worker"
                        >
                          <UserPlus className="h-4 w-4" />
                        </button>
                      )}
                      {canDeassignOrder(order) && onDeassignOrder && (
                        <button
                          onClick={() => onDeassignOrder(order)}
                          className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50"
                          title="Deassign worker"
                        >
                          <UserMinus className="h-4 w-4" />
                        </button>
                      )}
                      {canCancelOrder(order) && onCancelOrder && (
                        <button
                          onClick={() => onCancelOrder(order)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="Cancel order"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {orders.length === 0 && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
          <p className="mt-1 text-sm text-gray-500">Upload a file to get started.</p>
        </div>
      )}
    </div>
  );
};

export default OrderTable;
